import React, { useContext } from "react";
import { RouteComponentProps } from "react-router-dom";
import { FEATURE_FLAG_NAME, OBJECT_TYPE } from "common/constants";
import { useGetResidentCustomObjectInfoQuery } from "../../../generated/graphql";
import { useIsScreenSize, useWindowSize } from "../../../hooks/useWindowSize";
import { spacing } from "../../../stitches.config";
import { setTitle } from "../../../utils/title";
import { AuthContext } from "../../Authorization/AuthContext";
import { ObjectCarousel } from "../../Common/Carousel/Carousel";
import Icon, { ICON_COLORS, Icons } from "../../Common/Icons";
import { LayerContextProvider } from "../../Maps/layers";
import MapPreview from "../../Maps/Preview";
import { Bounds, calculateViewport } from "../../Maps/utils/viewportHook";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { PageSubheader } from "../PageSubheader";
import { PrintButton } from "../PrintButton";
import {
  AddressInfoContainer,
  CarouselContainer,
  LoadingContainer,
  MapboxAttributionContainer,
  MapboxIconContainer,
  PropertyDetailsContainer,
} from "../Property/__styles__/PropertyPage";

interface URLParams {
  customMapId: string;
  geometryId: string;
}

interface Props extends RouteComponentProps<URLParams> {
  print: typeof window.print;
}

const CustomObjectPage = ({ match, print = window.print }: Props) => {
  const { geometryId } = match.params;
  const { account } = useContext(AuthContext);

  const { data, loading } = useGetResidentCustomObjectInfoQuery({
    variables: { geometryId, accountId: account!.id },
    fetchPolicy: "network-only",
  });

  const { width } = useWindowSize();
  const isSmallScreen = useIsScreenSize("mobile", "tablet");
  const mapHeight = isSmallScreen ? width * 0.52 : 400;

  if (loading) {
    return (
      <LoadingContainer>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </LoadingContainer>
    );
  }

  if (!data || !data.customMapGeometry || !data.account) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.CUSTOM_OBJECT} />;
  }

  const { label, interiorPoint, coverImageDocumentUploadId } =
    data.customMapGeometry;
  const customMapId = data.customMapGeometry.customMap.id;
  const customMapName = data.customMapGeometry.customMap.name;

  const latitude = interiorPoint?.[1];
  const longitude = interiorPoint?.[0];

  const bounds = data.customMapGeometry.bounds as Bounds | null;
  const zoom =
    bounds ?
      calculateViewport({
        viewport: { bounds },
        infoPanelOpen: false,
        isDesktop: !isSmallScreen,
      }).viewport.zoom
    : undefined;

  const customMap = account!.customMaps.find(m => m.id === customMapId);
  const customMapsLayer =
    customMap ?
      { maps: [customMap], value: { customMapId, geometryId } }
    : undefined;

  setTitle(label);

  return (
    <LayerContextProvider
      account={data.account}
      noLayerPanel={true}
      defaultState={{
        customMaps: { [customMapId]: true },
      }}
    >
      <Layout hasGutters={true}>
        <div style={{ paddingBottom: spacing.m.value }}>
          <PageSubheader
            showDivider={false}
            additionalControls={<PrintButton print={print} />}
          />
        </div>

        {latitude && longitude && (
          <MapPreview
            height={`${mapHeight}px`}
            width={"100%"}
            latitude={latitude}
            longitude={longitude}
            zoom={zoom}
            account={data.account!}
            customMaps={customMapsLayer}
          />
        )}

        <MapboxAttributionContainer>
          <MapboxIconContainer>
            <Icon icon={Icons.MAPBOX_ATTRIBUTION} />
          </MapboxIconContainer>
          <span>&copy; Mapbox, &copy; OpenStreetMap</span>
        </MapboxAttributionContainer>
        <PropertyDetailsContainer>
          <AddressInfoContainer>
            <h1>{label}</h1>
            <h2>{customMapName}</h2>
          </AddressInfoContainer>
          <CarouselContainer>
            <ObjectCarousel
              label={label}
              objectId={geometryId}
              objectType={OBJECT_TYPE.CUSTOM_MAP_GEOMETRY}
              coverImageDocumentUploadId={coverImageDocumentUploadId ?? null}
              size="small"
            />
          </CarouselContainer>
        </PropertyDetailsContainer>
      </Layout>
    </LayerContextProvider>
  );
};

const CustomObjectWrapper = (props: Props) => {
  const { isFeatureEnabled } = useContext(AuthContext);

  if (!isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_CUSTOM_OBJECTS)) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.CUSTOM_OBJECT} />;
  }

  return <CustomObjectPage {...props} />;
};

export default CustomObjectWrapper;
